import { memo, useCallback, useState } from 'react';
import { getIntegrationProviderLogo } from '../constants';
import { initialsOf } from '../utils';

/** Tailwind box + initials size — aligned with IconHighlight densities; xl = Featured hero. */
const SIZE_CLASS = {
  small: 'h-8 w-8 text-[12px]',
  medium: 'h-10 w-10 text-[15px]',
  // Prototype `.logo-lg` is 48×48; keep that for Provider Detail.
  large: 'h-12 w-12 text-[18px]',
  xl: 'h-[88px] w-[88px] text-[33px]',
};

/**
 * Brand logo for an integration provider.
 *
 * Lives in the domain (not `@aimsos/ui`) on purpose: `EntityList`'s `icon` slot
 * expects a lucide component and its `avatar` slot is `rounded-full`/`object-cover`
 * — which crops non-square brand marks. A square container + `object-contain`
 * guarantees no cropping. Falls back to initials when the slug has no asset or
 * the image fails to load. Sizing is Tailwind-only (no inline styles).
 */
function IntegrationProviderLogo({ slug, name, size = 'medium', className = '' }) {
  const src = getIntegrationProviderLogo(slug);
  const [failed, setFailed] = useState(false);

  // In a list React recycles the instance; reset the failure flag when the
  // provider changes so a previous error does not stick to the next logo.
  const [prevSlug, setPrevSlug] = useState(slug);
  if (slug !== prevSlug) {
    setPrevSlug(slug);
    setFailed(false);
  }

  const handleError = useCallback(() => setFailed(true), []);

  const sizeClass = SIZE_CLASS[size] ?? SIZE_CLASS.medium;
  const boxClass = [
    'flex shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-edge bg-surface-elevated',
    sizeClass,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const showImage = src && !failed;

  return (
    <span className={boxClass}>
      {showImage ? (
        <img
          src={src}
          alt=""
          aria-hidden="true"
          draggable={false}
          onError={handleError}
          className="h-[68%] w-[68%] object-contain"
        />
      ) : (
        <span aria-hidden="true" className="select-none font-semibold leading-none text-content-secondary">
          {initialsOf(name)}
        </span>
      )}
    </span>
  );
}

export default memo(IntegrationProviderLogo);
